<script lang="ts">
	import {
		type Achievement,
		type AchievementHeadlineInfo,
		AchievementProgressState
	} from '../../../domain/Achievement';
	import AchievementProgress from '$components/home/achievement/elements/AchievementProgress.svelte';
	import AchievementBody from '$components/home/achievement/elements/AchievementBody.svelte';
	import {
		calculateExpirationStatus,
		formatPreviousDayForAriaLabel,
		formatPreviousDayWithDayAndMonth
	} from '$services/DateFormatter';
	import { ExpiryStatus } from '../../../domain/ExpiryStatus';
	import { instructionSheet } from '$components/home/SheetData';
	import AchievementFooter from '$components/home/achievement/elements/AchievementFooter.svelte';
	import { createEventDispatcher } from 'svelte';
	import { activateAchievement } from '$services/achievement/AchievementService';
	import AchievementBaseCard from '$components/home/achievement/AchievementBaseCard.svelte';
	import AchievementTrackingService from '$services/tracking/AchievementTrackingService.js';
	import { ActivationResult, ActivationSource } from '$services/tracking/Activation';

	let { achievement, index, onclick, isHorizontal = false, display, onViewPortCallback }: {
		achievement: Achievement,
		index: number,
		onclick: Function,
		isHorizontal?: boolean,
		display: 'row' | 'grid',
		onViewPortCallback?: Function
	} = $props();
	const dispatch = createEventDispatcher();
	const achievementTrackingService = new AchievementTrackingService();

	let loading: boolean = $state(false);

	//@ts-ignore
	const processAchievementActivation = async (event) => {
		event.stopPropagation();
		loading = true;
		const activationResource = await activateAchievement(achievement.achievementId);
		loading = activationResource.loading;
		await achievementTrackingService.sendAchievementActivationFeatureTracking(
			achievement,
			ActivationSource.Card,
			activationResource.success ? ActivationResult.Success : ActivationResult.Failure
		);

		if (!activationResource.success) {
			dispatch('achievementActivationFailed', achievement);
			return;
		}

		if (activationResource.state == AchievementProgressState.ACTIVATED) {
			dispatch('achievementActivated', achievement);
		}
	};

	const getHeadline = (progressEndDate: string): AchievementHeadlineInfo => {
		const expirationStatus = calculateExpirationStatus(progressEndDate);
		if (expirationStatus === ExpiryStatus.TOMORROW) {
			return {
				text: 'Nur bis morgen'
			};
		} else if (expirationStatus === ExpiryStatus.TODAY) {
			return {
				text: 'Nur noch heute'
			};
		} else {
			const visibleDate = formatPreviousDayWithDayAndMonth(progressEndDate);
			const accesibleDate = formatPreviousDayForAriaLabel(progressEndDate);

			return {
				text: `Nur bis ${visibleDate}`,
				ariaLabel: `Nur bis ${accesibleDate}`
			};
		}
	};

	const headlineInfo: AchievementHeadlineInfo = getHeadline(achievement.content.progressEndDate);

</script>

<AchievementBaseCard achievement={achievement} display={display} index={index+1} {onViewPortCallback} >
	<oc-card-v2
		id="achievement-card-{index+1}"
		data-testid="achievement-created-card"
		as-button=""
		class="card"
		data-oc-sheet-v1-create={JSON.stringify(instructionSheet(achievement.achievementId, "created-instructions-sheet"))}
		onclick={() => onclick()}
	>
		<div
			class="oc-p-100 card__content"
			class:card__content--horizontal={isHorizontal}
			data-testid="achievement-created-card-content">
			<div class="card__body">
				<AchievementProgress completionGoal={achievement.content.card.completionGoal}
					currentProgress={achievement.progress.current}
					progressColor={achievement.content.card.progressBarColor} />
				<AchievementBody
					description={achievement.content.card.description}
					title={achievement.content.card.title}>
					<div aria-label={headlineInfo.ariaLabel} class="oc-copy-75 text-secondary">
						{headlineInfo.text}
					</div>
				</AchievementBody>

			</div>
			<AchievementFooter
				compensationAmount={achievement.content.card.compensationAmount}
				compensationHeadline={'Verdiene'}
				isHorizontal={isHorizontal}>
				<oc-button-v1
					data-testid="achievement-created-card-button"
					oc-aria-label="Challenge aktivieren"
					icon-type-right="plus"
					size="50"
					variant="primary"
					onclick={processAchievementActivation}
					{loading}
				>
					Jetzt aktivieren
				</oc-button-v1>
			</AchievementFooter>
		</div>
	</oc-card-v2>
</AchievementBaseCard>

<style lang="scss">
  @use '@otto-ec/otto-components-utils/scss/mixins' as mixins;
  @use "@otto-ec/design-tokens/tokens";

  .card {
    &__content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;

      &--horizontal {
        @include mixins.breakpoint(mixins.$breakpoint-l) {
          flex-direction: row;
        }
      }
    }

    &__body {
      display: flex;
      align-items: center;
    }
  }

  .text-secondary {
    color: tokens.$oc-semantic-color-text-secondary
  }
</style>
